import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MessageSquare, 
  Send, 
  Loader2,
  User,
  Globe
} from 'lucide-react';
import { getChats, getMessages, sendMessage } from '../services/api';

const LANGUAGES = [
  { value: '', label: 'Keine Übersetzung' },
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'Englisch' },
  { value: 'tr', label: 'Türkisch' },
  { value: 'uk', label: 'Ukrainisch' },
  { value: 'ar', label: 'Arabisch' },
  { value: 'fr', label: 'Französisch' },
  { value: 'es', label: 'Spanisch' },
  { value: 'pl', label: 'Polnisch' },
  { value: 'ru', label: 'Russisch' },
];

const Messages = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.chat_id);
    }
  }, [selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      const res = await getChats();
      setChats(res.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const res = await getMessages(chatId);
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    setSending(true);
    try {
      const res = await sendMessage({
        chat_id: selectedChat.chat_id,
        content: newMessage,
        target_language: targetLanguage || undefined
      });
      setMessages([...messages, res.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-8rem)]" data-testid="messages-page">
        <h1 className="text-3xl font-heading font-bold text-foreground mb-6">Nachrichten</h1>

        <div className="grid md:grid-cols-3 gap-6 h-[calc(100%-4rem)]">
          {/* Chat List */}
          <Card className="md:col-span-1">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-14rem)]">
                {chats.length > 0 ? (
                  <div className="divide-y divide-border">
                    {chats.map((chat) => (
                      <button
                        key={chat.chat_id}
                        onClick={() => setSelectedChat(chat)}
                        className={`w-full p-4 text-left hover:bg-muted transition-colors ${
                          selectedChat?.chat_id === chat.chat_id ? 'bg-muted' : ''
                        }`}
                        data-testid={`chat-${chat.chat_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {chat.other_participant?.picture ? (
                              <img 
                                src={chat.other_participant.picture} 
                                alt={chat.other_participant.name}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {chat.other_participant?.name || 'Unbekannt'}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {chat.last_message || 'Keine Nachrichten'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground text-sm">
                      Keine Chats vorhanden
                    </p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="md:col-span-2 flex flex-col">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {selectedChat.other_participant?.picture ? (
                        <img 
                          src={selectedChat.other_participant.picture} 
                          alt={selectedChat.other_participant.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {selectedChat.other_participant?.name || 'Unbekannt'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${
                          msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === user?.user_id
                              ? 'bg-primary text-white'
                              : 'bg-muted text-foreground'
                          }`}
                        >
                          <p>{msg.content}</p>
                          {msg.translated_content && (
                            <div className="mt-2 pt-2 border-t border-white/20 text-xs opacity-80">
                              <Globe className="w-3 h-3 inline mr-1" />
                              {msg.translated_content}
                            </div>
                          )}
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user?.user_id ? 'text-white/70' : 'text-muted-foreground'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSend} className="p-4 border-t border-border space-y-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <Select value={targetLanguage || "none"} onValueChange={(val) => setTargetLanguage(val === "none" ? "" : val)}>
                      <SelectTrigger className="w-48 h-8 text-xs" data-testid="translate-select">
                        <SelectValue placeholder="Übersetzen nach..." />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(lang => (
                          <SelectItem key={lang.value || "none"} value={lang.value || "none"}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {targetLanguage && targetLanguage !== '' && (
                      <span className="text-xs text-primary">
                        Nachricht wird automatisch übersetzt
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Nachricht schreiben..."
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={sending || !newMessage.trim()}
                      className="rounded-full"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    Wählen Sie einen Chat aus
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Messages;
